import {
    Typography, Tab, Box,
    ThemeProvider, useTheme, Card, CardContent, Divider, Chip, CardHeader, FormControl, Select, MenuItem, Skeleton,
    Dialog, DialogContent, DialogTitle, IconButton
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { lightenHexColor } from '../../dwi-functions';
import { NumericFormat } from 'react-number-format';
import TrendChart from '../../report-components/trend-chart';
import ReactApexChart from 'react-apexcharts';
import { applyApexBrandGradient } from '../../system/apex-gradient';
import ResponsiveTabs from '../../system/responsive-tabs';
import moment from 'moment';
import Comments from '../../views/comments';
import { AppVarsContext } from '../../context/AppVarsContext';
import Native from './native';
import Display from './display';
import Video from './video';
import Audio from './audio';
import Footfall from './footfall';
import ViMarcTableCard from '../../system/vimarc-table-card';
import { getProgrammaticCompletionMetrics } from './completion-metrics';
import { buildApexTooltip, renderTrendTooltip } from '../../system/apex-tooltip';
import CloseIcon from '@mui/icons-material/Close';
import ReportPerformanceOverview, { ReportSectionIntro } from '../shared/ReportPerformanceOverview';
import { createReportSurfaceTheme, getReportPageSx, getReportSurfaceSx, getAnalyticsReportTabsSx, reportColors, styleReportChart } from '../shared/report-design';
import ReportQuestionAssistant from '../../system/controls/report-question-assistant';
import { resolveShowCost } from '../../system/report-visibility';
import { formatUsd } from '../../system/report-formatters';













function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={0}>
                    {children}
                </Box>
            )}
        </Box>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const isLikelyUrl = (value) => {
    if (!value) return false;
    const text = String(value).trim();
    return /^https?:\/\//i.test(text);
};

const collectUrlStrings = (obj) => {
    const urls = [];
    if (!obj || typeof obj !== 'object') return urls;
    Object.values(obj).forEach((val) => {
        if (isLikelyUrl(val)) {
            urls.push(String(val));
            return;
        }
        if (Array.isArray(val)) {
            val.forEach((item) => {
                if (isLikelyUrl(item)) urls.push(String(item));
            });
        }
    });
    return urls;
};

const normalizeAssetUrls = (source) => {
    const output = [];
    const push = (val) => {
        if (val == null) return;
        if (Array.isArray(val)) {
            val.forEach((item) => push(item));
            return;
        }
        if (typeof val === 'object') {
            const url =
                val.IMAGE_ASSET_URL ||
                val.IMAGE_URL ||
                val.THUMBNAIL_URL ||
                val.thumbnail_url ||
                val.thumbnail ||
                val.ASSET_URL ||
                val.asset_url ||
                val.PREVIEW_URL ||
                val.preview_url ||
                val.CREATIVE_URL ||
                val.URL ||
                val.url ||
                val.image_url ||
                val.image ||
                val.name;
            if (isLikelyUrl(url)) output.push(String(url));
            if (isLikelyUrl(val.name)) output.push(String(val.name));
            collectUrlStrings(val).forEach((urlItem) => output.push(urlItem));
            return;
        }
        if (isLikelyUrl(val)) output.push(String(val));
    };
    push(source);
    return output.filter(Boolean);
};

const getAssetPreviewUrl = (row) => {
    const urls = normalizeAssetUrls([
        row?.IMAGE_ASSET_URL,
        row?.IMAGE_URL,
        row?.IMAGE_URLS,
        row?.ASSET_URL,
        row?.ASSET_URLS,
        row?.CREATIVE_URL,
        row?.THUMBNAIL_URL,
        row?.thumbnail,
        row?.PREVIEW_URL,
        row?.URL,
        row?.url,
        row?.ASSETS,
        row?.ASSET,
        row?.creative,
        row?.CREATIVE
    ]);
    if (urls.length) return urls[0];
    const looseUrls = collectUrlStrings(row);
    return looseUrls[0] || '';
};

const normalizeFootfallPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.data && typeof payload.data === 'object') return payload.data;
    return payload;
};

const parseReportSettings = (settings) => {
    if (!settings) return null;
    if (typeof settings === 'object') return settings;
    try {
        return JSON.parse(settings);
    } catch (error) {
        return null;
    }
};

const isFootfallPayload = (payload) => {
    const normalized = normalizeFootfallPayload(payload);
    return Boolean(
        normalized &&
        typeof normalized === 'object' &&
        (
            normalized.totals ||
            Array.isArray(normalized.campaigns) ||
            normalized.breakdowns
        )
    );
};

const PROGRAMMATIC_CHANNELS = [
    { key: 'display', label: 'Display' },
    { key: 'native', label: 'Native' },
    { key: 'video', label: 'Video' },
    { key: 'audio', label: 'Audio' },
    { key: 'ctv', label: 'CTV' }
];

const getProgrammaticCampaignMetric = (row, key) => {
    if (key === 'impressions') return Number(row?.IMPRESSIONS ?? row?.impressions ?? 0) || 0;
    if (key === 'conversions') return Number(row?.CONVERSIONS ?? row?.conversions ?? 0) || 0;
    return 0;
};

const keepRetailProgrammaticCampaign = (row) => (
    getProgrammaticCampaignMetric(row, 'impressions') > 0
    && getProgrammaticCampaignMetric(row, 'conversions') > 0
);

const filterRetailProgrammaticChannelMap = (channelMap, enabled) => {
    if (!enabled || !channelMap || typeof channelMap !== 'object') return channelMap;
    return Object.entries(channelMap).reduce((output, [key, value]) => {
        if (!value || typeof value !== 'object') {
            output[key] = value;
            return output;
        }
        output[key] = {
            ...value,
            campaigns: Array.isArray(value.campaigns)
                ? value.campaigns.filter(keepRetailProgrammaticCampaign)
                : value.campaigns
        };
        return output;
    }, {});
};

const filterRetailProgrammaticPayload = (payload, enabled) => {
    if (!enabled || !payload || typeof payload !== 'object') return payload;
    return {
        ...payload,
        channel: filterRetailProgrammaticChannelMap(payload.channel, true),
        previousChannel: filterRetailProgrammaticChannelMap(payload.previousChannel, true)
    };
};

const StackAdapt = (props) => {
    const retailMode = Boolean(props.retailMode);
    const awarenessMode = Boolean(props.awarenessMode || props.retailMode || props.vimarcMode);
    const [campaignData, setCampaignData] = useState();
    const [data, setStackAdaptData] = useState();
    const [footfallData, setFootfallData] = useState(null);
    const [loadingReport, setLoadingReport] = useState(true);
    const [typeSelect] = useState([{ CAMPAIGN_TYPE: 'native' }]);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState('');
    const [lightboxTitle, setLightboxTitle] = useState('');

    const [value, setValue] = useState('overview');
    const [admintoken] = useState(props.admintoken);
    const [combinedCellWidth, setCombinedCellWidth] = useState(0);
    const pDate = new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('sDate'))) + "-" + new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('eDate')));
    const classes = {};
    const actionCellRef = useRef(null);
    const firstCellRef = useRef(null);
    const [types, setTypes] = useState([]);
    const [open, setOpen] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const { appVars, appLoading, appID } = useContext(AppVarsContext);

    const [client, setClient] = useState({
        clientId: sessionStorage.getItem('apiID'),
        apiPort: sessionStorage.getItem('aPort')
    });



    const handleOpen = (url) => {
        setVideoUrl(url); // Set the clicked video URL
        setOpen(true); // Open the dialog
    };

    const handleClose = () => {
        setOpen(false);
        setVideoUrl(''); // Clear the URL when closing
    };

    const reportRef1 = useRef()
    const reportRef2 = useRef()
    const reportRef3 = useRef()
    const reportRef4 = useRef()

    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const primaryColor = theme.palette.primary?.main || '#6aa7ff';
    const secondaryColor = theme.palette.secondary?.main || primaryColor;
    const surfaceCardSx = useMemo(() => getReportSurfaceSx(theme), [theme]);
    const tabsShellSx = useMemo(() => ({ ...getReportSurfaceSx(theme), px: 1 }), [theme]);
    const tabsSx = getAnalyticsReportTabsSx();
    const pageShellSx = useMemo(() => ({ ...getReportPageSx(theme), '& .stackadapt-executive-content .MuiPaper-root': getReportSurfaceSx(theme) }), [theme]);

    const parsedReportSettings = useMemo(
        () => parseReportSettings(props.reportProfile?.settings),
        [props.reportProfile?.settings]
    );
    const stackadaptSettings = useMemo(
        () => parsedReportSettings?.ad_sets?.stackadapt || null,
        [parsedReportSettings]
    );
    const hasFootfallGraphqlToken = useMemo(() => {
        const tokenValue = stackadaptSettings?.graphql_token;
        if (typeof tokenValue === 'string') return tokenValue.trim().length > 0;
        return Boolean(tokenValue);
    }, [stackadaptSettings?.graphql_token]);
    const effectiveReportProfile = useMemo(() => {
        if (!props.reportProfile) return props.reportProfile;
        return {
            ...props.reportProfile,
            showCost: awarenessMode ? false : resolveShowCost(props.reportProfile)
        };
    }, [awarenessMode, props.reportProfile]);
    const showCost = resolveShowCost(effectiveReportProfile);
    const showRevenue = !awarenessMode;
    const stackAdaptAssistantConfig = useMemo(() => ({
        activeView: String(value || 'overview')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase())
    }), [value]);

    const renderLoadingSkeleton = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Array.from({ length: 8 }).map((_, index) => (
                    <Skeleton
                        key={`stackadapt-skel-${index}`}
                        variant="rectangular"
                        height={90}
                        sx={{ flex: '1 1 160px', borderRadius: 1 }}
                    />
                ))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Skeleton variant="rectangular" height={220} sx={{ flex: '1 1 320px', borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={220} sx={{ flex: '1 1 320px', borderRadius: 1 }} />
            </Box>
            <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 1 }} />
        </Box>
    );

    const openLightbox = (src, title) => {
        if (!src) return;
        setLightboxSrc(src);
        setLightboxTitle(title || 'Creative Preview');
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setLightboxSrc('');
        setLightboxTitle('');
    };


    const headerStyleObj = {
        textAlign: 'center',
        backgroundColor: lightenHexColor(theme.palette.primary.main, 0),
        color: lightenHexColor(theme.palette.primary.light, 100),
        border: '1px solid ' + theme.palette.secondary.main,
        paddingTop: 5,
        paddingBottom: 5,
        maxWidth: 75,
    };


    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    var detailsOptions = {
        headerStyle: headerStyleObj,
        grouping: false,
        searchFieldVariant: "outlined",
        exportButton: true,
        exportAllData: true,
        search: false,
        // paging: (creativeData.length > 10),
        exportFileName: moment(new Date()).format("YYYYMMDD.hhmmss"),
        //  pageSizeOptions: [10, 25, 50, 100],
        showTitle: false,
        toolbar: true,
        sort: true
    };


    async function fetchReportData() {

        setLoadingReport(true);
        props.setLoading(true);
        setFootfallData(null);
        try {
            var myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");

            const aid = stackadaptSettings?.id;
            const rawPayload = {
                cid: sessionStorage.getItem('rid'),
                sdate: sessionStorage.getItem('sDate'),
                edate: sessionStorage.getItem('eDate'),
                token: props.reportProfile.saKey,
                aid
            };
            var raw = JSON.stringify(rawPayload);

            var requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: raw,
                redirect: 'follow'
            };

            const fetchFootfallData = async () => {
                if (!hasFootfallGraphqlToken) return null;
                const endpoints = [

                    '/stackadapt/footfall'

                ];

                for (const endpoint of endpoints) {
                    try {
                        const response = await fetch(`${appVars.api_url}${endpoint}`, requestOptions);
                        if (!response.ok) continue;
                        const json = await response.json();
                        if (isFootfallPayload(json)) {
                            return normalizeFootfallPayload(json);
                        }
                    } catch (error) {
                        continue;
                    }
                }

                return null;
            };

            const [campaignResult, footfallResult] = await Promise.allSettled([
                fetch(`${appVars.api_url}/stackadapt/campaigns/id`, requestOptions).then(async (response) => {
                    if (!response.ok) {
                        throw new Error(`Campaign request failed with status ${response.status}`);
                    }
                    return response.json();
                }),
                hasFootfallGraphqlToken ? fetchFootfallData() : Promise.resolve(null)
            ]);

            if (campaignResult.status !== 'fulfilled') {
                throw campaignResult.reason;
            }

            const json = filterRetailProgrammaticPayload(campaignResult.value, retailMode);
            setStackAdaptData(json);
            setCampaignData(json.channel);

            if (footfallResult.status === 'fulfilled') {
                setFootfallData(footfallResult.value);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoadingReport(false);
            props.setLoading(false);
        }

        //  const campTable = await fetch(`http://localhost:${client.apiPort}/stackadapt/campaigns`, requestOptions);
        /*
             const campTable = await fetch(`https://${client.clientId}.autogateapi.com:${client.apiPort}/stackadapt/campaigns`, requestOptions);
             //    console.log(props);
             campTable
                 .json()
                 .then((campaigns) => {
                     setCampaignData(campaigns);
     
                 })
                 .catch((err) => {
                     console.log(err);
                 });
          
                     const creativeTable = await fetch(`https://${client.clientId}.autogateapi.com:${client.apiPort}/stackadapt/creatives`, requestOptions);
                     //    console.log(props);
                     creativeTable
                         .json()
                         .then((creatives) => {
                             setCreativeData(creatives);
             
                         })
                         .catch((err) => {
                             console.log(err);
                         });
             
             */


    }
    useEffect(() => {

        if (actionCellRef.current && firstCellRef.current) {
            setCombinedCellWidth(actionCellRef.current.offsetWidth + firstCellRef.current.offsetWidth);
        }
        if (!appLoading) {
            fetchReportData();
            //  console.log(data);
        }
    }, [props, typeSelect, appLoading]);


    useEffect(() => {
        const handleStorageChange = () => {
            // console.log('Custom dc event received – refreshing campaign data');
            fetchReportData();
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, []);

    const safeNum = (value) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
    };

    const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(safeNum(value));
    const formatPercent = (value) => `${safeNum(value).toFixed(2)}%`;
    const formatCurrency = (value) => formatUsd(safeNum(value));

    const channelAvailability = useMemo(() => {
        const hasChannelData = (key) => {
            const channel = data?.channel?.[key] || campaignData?.[key];
            if (!channel) return false;
            const campaigns = channel?.campaigns;
            if (Array.isArray(campaigns) && campaigns.length) return true;
            const stats = channel?.stats;
            if (stats && typeof stats === 'object') {
                return Object.values(stats).some((metric) => safeNum(metric?.count) > 0);
            }
            const series = data?.seriesByChannel?.[key];
            if (Array.isArray(series) && series.length) return true;
            return false;
        };

        return {
            native: hasChannelData('native'),
            display: hasChannelData('display'),
            video: hasChannelData('video'),
            audio: hasChannelData('audio'),
            ctv: hasChannelData('ctv')
        };
    }, [data, campaignData]);

    useEffect(() => {
        const available = [
            'overview',
            ...(hasFootfallGraphqlToken ? ['footfall'] : []),
            ...(channelAvailability.native ? ['native'] : []),
            ...(channelAvailability.display ? ['display'] : []),
            ...(channelAvailability.video ? ['video'] : []),
            ...(channelAvailability.audio ? ['audio'] : []),
            ...(channelAvailability.ctv ? ['ctv'] : []),
            'notes'
        ];
        if (!available.includes(value)) {
            setValue('overview');
        }
    }, [channelAvailability, hasFootfallGraphqlToken, value]);

    const buildTrendRowsFromSeriesByChannel = (seriesByChannel) => {
        if (!seriesByChannel || typeof seriesByChannel !== 'object') return [];
        const rowsByDate = {};
        Object.values(seriesByChannel).forEach((series) => {
            if (!Array.isArray(series)) return;
            series.forEach((row) => {
                const date = row?.date ?? row?.x ?? row?.day;
                if (!date) return;
                if (!rowsByDate[date]) {
                    rowsByDate[date] = {
                        date,
                        impressions: 0,
                        clicks: 0,
                        conversions: 0,
                        cost: 0,
                        revenue: 0,
                    };
                }
                rowsByDate[date].impressions += safeNum(row?.impressions);
                rowsByDate[date].clicks += safeNum(row?.clicks);
                rowsByDate[date].conversions += safeNum(row?.conversions);
                rowsByDate[date].cost += safeNum(row?.cost);
                rowsByDate[date].revenue += safeNum(row?.revenue);
            });
        });

        const rows = Object.values(rowsByDate);
        rows.sort((a, b) => {
            const aTime = new Date(a.date).getTime();
            const bTime = new Date(b.date).getTime();
            if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
            return aTime - bTime;
        });
        return rows;
    };

    const buildSeries = (rows, key, calcFn) => {
        if (!Array.isArray(rows)) return [];
        return rows.map((row) => {
            const x = row?.date ?? row?.x ?? row?.day ?? '';
            const y = calcFn ? calcFn(row) : safeNum(row?.[key]);
            return { x, y };
        });
    };

    const computeTotals = (totals, rows, fallbackTotals) => {
        const sumRows = (key) => rows.reduce((sum, row) => sum + safeNum(row?.[key]), 0);
        const resolveValue = (primary, key) => {
            const direct = safeNum(primary);
            if (direct > 0) return direct;
            const fromRows = sumRows(key);
            if (fromRows > 0) return fromRows;
            return safeNum(fallbackTotals?.[key]);
        };

        const impressions = resolveValue(totals?.imp ?? totals?.impressions, 'impressions');
        const clicks = resolveValue(totals?.click ?? totals?.clicks, 'clicks');
        const conversions = resolveValue(totals?.conv ?? totals?.conversions, 'conversions');
        const cost = resolveValue(totals?.cost, 'cost');
        const revenue = resolveValue(totals?.revenue, 'revenue');

        const ctr = safeNum(totals?.ctr ?? (impressions ? (clicks / impressions) * 100 : 0));
        const convrate = safeNum(totals?.cvr ?? totals?.conv_rate ?? (clicks ? (conversions / clicks) * 100 : 0));
        const cpc = safeNum(totals?.ecpc ?? totals?.cpc ?? (clicks ? cost / clicks : 0));
        const cpm = safeNum(totals?.ecpm ?? totals?.cpm ?? (impressions ? (cost / impressions) * 1000 : 0));
        const avgGift = safeNum(totals?.avgGift ?? totals?.avg_gift ?? (conversions ? revenue / conversions : 0));

        return { impressions, clicks, conversions, cost, revenue, ctr, convrate, cpc, cpm, avgGift };
    };

    const channelTotals = useMemo(() => {
        const channelSource = data?.channel || campaignData;
        if (!channelSource) return null;
        const totals = { impressions: 0, clicks: 0, conversions: 0, cost: 0, revenue: 0 };
        Object.values(channelSource).forEach((channel) => {
            const stats = channel?.stats || channel?.totals || channel?.total_stats || null;
            if (!stats) return;
            totals.impressions += safeNum(stats?.impressions?.count ?? stats?.impressions);
            totals.clicks += safeNum(stats?.clicks?.count ?? stats?.clicks);
            totals.conversions += safeNum(stats?.conversions?.count ?? stats?.conversions);
            totals.cost += safeNum(stats?.cost?.count ?? stats?.cost);
            totals.revenue += safeNum(stats?.revenue?.count ?? stats?.revenue);
        });
        return totals;
    }, [data, campaignData]);

    const combined = useMemo(() => {
        if (!data) return null;

        const trendRows = Array.isArray(data?.series) && data.series.length
            ? data.series
            : buildTrendRowsFromSeriesByChannel(data?.seriesByChannel);

        const prevTrendRows = Array.isArray(data?.previousSeries) && data.previousSeries.length
            ? data.previousSeries
            : buildTrendRowsFromSeriesByChannel(data?.previousSeriesByChannel);

        const currentTotals = computeTotals(data?.totals, trendRows, channelTotals);
        const previousTotals = computeTotals(data?.previousTotals, prevTrendRows, null);

        const series = {
            impressions: buildSeries(trendRows, 'impressions'),
            clicks: buildSeries(trendRows, 'clicks'),
            conversions: buildSeries(trendRows, 'conversions'),
            cost: buildSeries(trendRows, 'cost'),
            revenue: buildSeries(trendRows, 'revenue'),
            ctr: buildSeries(trendRows, null, (row) => {
                const impressions = safeNum(row?.impressions);
                const clicks = safeNum(row?.clicks);
                return impressions ? (clicks / impressions) * 100 : 0;
            }),
            convrate: buildSeries(trendRows, null, (row) => {
                const clicks = safeNum(row?.clicks);
                const conversions = safeNum(row?.conversions);
                return clicks ? (conversions / clicks) * 100 : 0;
            }),
            cpc: buildSeries(trendRows, null, (row) => {
                const clicks = safeNum(row?.clicks);
                const cost = safeNum(row?.cost);
                return clicks ? cost / clicks : 0;
            }),
            cpm: buildSeries(trendRows, null, (row) => {
                const impressions = safeNum(row?.impressions);
                const cost = safeNum(row?.cost);
                return impressions ? (cost / impressions) * 1000 : 0;
            }),
            avgGift: buildSeries(trendRows, null, (row) => {
                const conversions = safeNum(row?.conversions);
                const revenue = safeNum(row?.revenue);
                return conversions ? revenue / conversions : 0;
            }),
        };

        const previousSeries = {
            impressions: buildSeries(prevTrendRows, 'impressions'),
            clicks: buildSeries(prevTrendRows, 'clicks'),
            conversions: buildSeries(prevTrendRows, 'conversions'),
            cost: buildSeries(prevTrendRows, 'cost'),
            revenue: buildSeries(prevTrendRows, 'revenue'),
            ctr: buildSeries(prevTrendRows, null, (row) => {
                const impressions = safeNum(row?.impressions);
                const clicks = safeNum(row?.clicks);
                return impressions ? (clicks / impressions) * 100 : 0;
            }),
            convrate: buildSeries(prevTrendRows, null, (row) => {
                const clicks = safeNum(row?.clicks);
                const conversions = safeNum(row?.conversions);
                return clicks ? (conversions / clicks) * 100 : 0;
            }),
            cpc: buildSeries(prevTrendRows, null, (row) => {
                const clicks = safeNum(row?.clicks);
                const cost = safeNum(row?.cost);
                return clicks ? cost / clicks : 0;
            }),
            cpm: buildSeries(prevTrendRows, null, (row) => {
                const impressions = safeNum(row?.impressions);
                const cost = safeNum(row?.cost);
                return impressions ? (cost / impressions) * 1000 : 0;
            }),
            avgGift: buildSeries(prevTrendRows, null, (row) => {
                const conversions = safeNum(row?.conversions);
                const revenue = safeNum(row?.revenue);
                return conversions ? revenue / conversions : 0;
            }),
        };

        return {
            trendRows,
            currentTotals,
            previousTotals,
            series,
            previousSeries
        };
    }, [data]);

    const channelSummary = useMemo(() => {
        if (!data?.channel) return [];
        return PROGRAMMATIC_CHANNELS
            .map(({ key, label }) => {
                const stats = data?.channel?.[key]?.stats;
                if (!stats) return null;
                const impressions = safeNum(stats?.impressions?.count);
                const clicks = safeNum(stats?.clicks?.count);
                const conversions = safeNum(stats?.conversions?.count);
                const cost = safeNum(stats?.cost?.count);
                const revenue = safeNum(stats?.revenue?.count);
                const ctr = safeNum(stats?.ctr?.count ?? (impressions ? (clicks / impressions) * 100 : 0));
                const convrate = safeNum(stats?.convrate?.count ?? (clicks ? (conversions / clicks) * 100 : 0));
                const cpc = safeNum(stats?.cpc?.count ?? (clicks ? cost / clicks : 0));
                const cpm = safeNum(stats?.cpm?.count ?? (impressions ? (cost / impressions) * 1000 : 0));
                const avgGift = safeNum(stats?.avgGift?.count ?? (conversions ? revenue / conversions : 0));
                const roas = cost ? revenue / cost : 0;
                return {
                    key,
                    label,
                    impressions,
                    clicks,
                    conversions,
                    cost,
                    revenue,
                    ctr,
                    convrate,
                    cpc,
                    cpm,
                    avgGift,
                    roas
                };
            })
            .filter(Boolean);
    }, [data]);

    const truncateLabel = (value, max = 36) => {
        const text = String(value || '');
        if (text.length <= max) return text;
        return `${text.slice(0, max - 3)}...`;
    };

    const campaignRows = useMemo(() => {
        if (!data?.channel && !campaignData) return [];
        const rows = [];
        PROGRAMMATIC_CHANNELS.forEach(({ key, label }) => {
            const campaigns = data?.channel?.[key]?.campaigns || campaignData?.[key]?.campaigns || [];
            if (!Array.isArray(campaigns)) return;
            campaigns.forEach((row) => {
                const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
                const clicks = safeNum(row?.CLICKS ?? row?.clicks);
                const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
                const revenue = safeNum(row?.REVENUE ?? row?.revenue);
                const cost = safeNum(row?.COST ?? row?.cost);
                const ctr = safeNum(row?.CTR ?? row?.ctr ?? (impressions ? (clicks / impressions) * 100 : 0));
                const convrate = safeNum(row?.CONV_RATE ?? row?.CONVRATE ?? row?.convrate ?? (clicks ? (conversions / clicks) * 100 : 0));
                const cpc = safeNum(row?.CPC ?? row?.cpc ?? (clicks ? cost / clicks : 0));
                const cpm = safeNum(row?.CPM ?? row?.cpm ?? (impressions ? (cost / impressions) * 1000 : 0));
                const roas = safeNum(row?.ROAS ?? row?.roas ?? (cost ? revenue / cost : 0));
                const name = row?.name || row?.campaign || row?.CAMPAIGN || row?.label || 'Campaign';
                const completionMetrics = getProgrammaticCompletionMetrics(row, key);

                rows.push({
                    id: row?.id || row?.ID || row?.campaign_id || name,
                    name,
                    shortName: truncateLabel(name),
                    channelKey: key,
                    channel: label,
                    assetUrl: getAssetPreviewUrl(row),
                    impressions,
                    clicks,
                    conversions,
                    revenue,
                    cost,
                    ctr,
                    convrate,
                    cpc,
                    cpm,
                    avgGift: conversions ? revenue / conversions : 0,
                    roas,
                    completedCount: completionMetrics.completedCount,
                    completionRate: completionMetrics.completionRate
                });
            });
        });

        return rows;
    }, [data, campaignData]);

    const topAdsByCtr = useMemo(() => {
        return campaignRows
            .slice()
            .filter((row) => row.impressions > 0 || row.clicks > 0)
            .sort((a, b) => b.ctr - a.ctr)
            .slice(0, 1);
    }, [campaignRows]);

    const topAdsByImpressions = useMemo(() => {
        return campaignRows
            .slice()
            .filter((row) => row.impressions > 0)
            .sort((a, b) => b.impressions - a.impressions)
            .slice(0, 1);
    }, [campaignRows]);

    const topAdsByConvRate = useMemo(() => {
        return campaignRows
            .slice()
            .filter((row) => row.clicks > 0)
            .sort((a, b) => b.convrate - a.convrate)
            .slice(0, 1);
    }, [campaignRows]);

    const metricChoices = useMemo(() => {
        const base = awarenessMode
            ? [
                { key: 'clicks', label: 'Clicks', format: 'number' },
                { key: 'impressions', label: 'Impressions', format: 'number' },
                { key: 'ctr', label: 'CTR', format: 'percent' },
                { key: 'conversions', label: 'Conversions', format: 'number' },
                { key: 'convrate', label: 'Conversion Rate', format: 'percent' }
            ]
            : [
                { key: 'conversions', label: 'Conversions', format: 'number' },
                { key: 'convrate', label: 'Conversion Rate', format: 'percent' },
                { key: 'ctr', label: 'CTR', format: 'percent' },
                { key: 'clicks', label: 'Clicks', format: 'number' },
                { key: 'impressions', label: 'Impressions', format: 'number' }
            ];
        if (showRevenue) {
            base.unshift({ key: 'revenue', label: 'Revenue', format: 'currency' });
        }
        if (showCost) {
            base.splice(2, 0, { key: 'cost', label: 'Spend', format: 'currency' });
            base.push(
                { key: 'cpc', label: 'CPC', format: 'currency' },
                { key: 'cpm', label: 'CPM', format: 'currency' },
                { key: 'roas', label: 'ROAS', format: 'ratio' }
            );
        }
        return base;
    }, [awarenessMode, showCost, showRevenue]);

    const defaultTopMetric = useMemo(() => {
        if (!campaignRows.length) return awarenessMode ? 'clicks' : 'conversions';
        if (awarenessMode) {
            const hasClicks = campaignRows.some((row) => row.clicks > 0);
            if (hasClicks) return 'clicks';
            const hasImpressions = campaignRows.some((row) => row.impressions > 0);
            if (hasImpressions) return 'impressions';
            return 'ctr';
        }
        const hasRevenue = showRevenue && campaignRows.some((row) => row.revenue > 0);
        if (hasRevenue) return 'revenue';
        const hasConversions = campaignRows.some((row) => row.conversions > 0);
        if (hasConversions) return 'conversions';
        const hasClicks = campaignRows.some((row) => row.clicks > 0);
        if (hasClicks) return 'clicks';
        return 'impressions';
    }, [campaignRows, awarenessMode, showRevenue]);

    const [topCampaignMetric, setTopCampaignMetric] = useState(defaultTopMetric);

    useEffect(() => {
        if (!metricChoices.length) return;
        const exists = metricChoices.some((choice) => choice.key === topCampaignMetric);
        if (!exists) {
            setTopCampaignMetric(defaultTopMetric);
        }
        if (!topCampaignMetric) {
            setTopCampaignMetric(defaultTopMetric);
        }
    }, [metricChoices, topCampaignMetric, defaultTopMetric]);

    const pickIdValue = (...values) => (
        values.find((value) => (
            (typeof value === 'string' && value.trim() !== '') || typeof value === 'number'
        )) || null
    );

    const getGroupId = (row) => pickIdValue(
        row?.ad_group_id,
        row?.adgroup_id,
        row?.adGroupId,
        row?.adgroupId,
        row?.AD_GROUP_ID,
        row?.ADGROUP_ID,
        row?.GROUP_ID,
        row?.group_id,
        row?.groupId,
        row?.id,
        row?.ID,
        row?.campaign_id,
        row?.campaignId,
        row?.campaignID
    );

    const topGroupIds = useMemo(() => {
        const ids = [
            ...topAdsByCtr.map((row) => getGroupId(row)),
            ...topAdsByConvRate.map((row) => getGroupId(row))
        ].filter(Boolean);
        return Array.from(new Set(ids));
    }, [topAdsByCtr, topAdsByConvRate]);

    const [creativeMap, setCreativeMap] = useState({});

    useEffect(() => {
        if (!appVars?.api_url) return;
        if (!topGroupIds.length) return;
        const missing = topGroupIds.filter((id) => !creativeMap[id]);
        if (!missing.length) return;
        let isActive = true;
        const fetchCreatives = async () => {
            const results = await Promise.all(
                missing.map(async (id) => {
                    try {
                        const raw = JSON.stringify({
                            campaign: id,
                            cid: sessionStorage.getItem('rid'),
                            sdate: sessionStorage.getItem('sDate'),
                            edate: sessionStorage.getItem('eDate')
                        });
                        const res = await fetch(`${appVars.api_url}/stackadapt/creatives/`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: raw,
                            redirect: 'follow'
                        });
                        const data = await res.json();
                        return [id, Array.isArray(data) ? data : []];
                    } catch (error) {
                        return [id, []];
                    }
                })
            );
            if (!isActive) return;
            setCreativeMap((prev) => {
                const next = { ...prev };
                results.forEach(([id, rows]) => {
                    next[id] = rows;
                });
                return next;
            });
        };
        fetchCreatives();
        return () => {
            isActive = false;
        };
    }, [topGroupIds, appVars?.api_url, creativeMap]);

    const buildCreativeMetrics = (row) => {
        const impressions = safeNum(row?.IMPRESSIONS ?? row?.impressions);
        const clicks = safeNum(row?.CLICKS ?? row?.clicks);
        const conversions = safeNum(row?.CONVERSIONS ?? row?.conversions);
        const cost = safeNum(row?.COST ?? row?.cost);
        const revenue = safeNum(row?.REVENUE ?? row?.revenue);
        const ctr = safeNum(row?.CTR ?? row?.ctr ?? (impressions ? (clicks / impressions) * 100 : 0));
        const convRate = safeNum(row?.CONV_RATE ?? row?.CONVRATE ?? row?.convrate ?? (clicks ? (conversions / clicks) * 100 : 0));
        const avgGift = safeNum(row?.AVG_GIFT ?? row?.avgGift ?? row?.avg_gift ?? (conversions ? revenue / conversions : 0));
        const roas = safeNum(row?.ROAS ?? row?.roas ?? (cost ? revenue / cost : 0));
        return { impressions, clicks, conversions, cost, revenue, ctr, convRate, avgGift, roas };
    };

    const getCreativeWinnerForGroup = (row, metricKey) => {
        const id = getGroupId(row);
        const creatives = id ? creativeMap[id] || [] : [];
        if (!creatives.length) return null;
        const scored = creatives
            .map((creative) => ({ creative, metrics: buildCreativeMetrics(creative) }))
            .filter((item) => item.metrics.impressions > 0 || item.metrics.clicks > 0);
        if (!scored.length) {
            return { creative: creatives[0], metrics: buildCreativeMetrics(creatives[0]) };
        }
            const key = metricKey === 'convRate'
                ? 'convRate'
                : (metricKey === 'impressions' ? 'impressions' : 'ctr');
        scored.sort((a, b) => b.metrics[key] - a.metrics[key]);
        return scored[0];
    };

    const getCreativeLabel = (row) => {
        const label = row?.creative || row?.CREATIVE || row?.CREATIVE_NAME || row?.NAME || row?.name || row?.AD_NAME || row?.AD_ID || row?.id;
        if (isLikelyUrl(label)) return 'Creative';
        return label || 'Creative';
    };

    const topCtrGroup = topAdsByCtr[0] || null;
    const topSecondaryGroup = (awarenessMode ? topAdsByImpressions : topAdsByConvRate)[0] || null;
    const topCtrWinner = useMemo(() => (
        topCtrGroup ? getCreativeWinnerForGroup(topCtrGroup, 'ctr') : null
    ), [topCtrGroup, creativeMap]);
    const topSecondaryWinner = useMemo(() => (
        topSecondaryGroup ? getCreativeWinnerForGroup(topSecondaryGroup, awarenessMode ? 'impressions' : 'convRate') : null
    ), [awarenessMode, topSecondaryGroup, creativeMap]);
    const ctrCreatives = topCtrGroup ? creativeMap[getGroupId(topCtrGroup)] : null;
    const secondaryCreatives = topSecondaryGroup ? creativeMap[getGroupId(topSecondaryGroup)] : null;

    const topCampaignsData = useMemo(() => {
        const activeMetric = topCampaignMetric || defaultTopMetric || 'conversions';
        const metricMeta = metricChoices.find((choice) => choice.key === activeMetric) || metricChoices[0];
        if (!campaignRows.length || !metricMeta) {
            return { metricKey: activeMetric, metricLabel: 'Conversions', campaigns: [], formatValue: () => '' };
        }

        const { key: metricKey, label: metricLabel, format } = metricMeta;
        const sorted = [...campaignRows].sort((a, b) => safeNum(b[metricKey]) - safeNum(a[metricKey]));
        const campaigns = sorted.slice(0, 6);
        const maxValue = Math.max(0, ...campaigns.map((row) => safeNum(row[metricKey])));
        const formatValue = (value) => {
            if (format === 'currency') return formatCurrency(value);
            if (format === 'percent') return formatPercent(value);
            if (format === 'ratio') return `${safeNum(value).toFixed(2)}x`;
            return formatNumber(value);
        };

        return { metricKey, metricLabel, campaigns, formatValue, maxValue };
    }, [campaignRows, metricChoices, topCampaignMetric, defaultTopMetric]);

    const topCampaignChart = useMemo(() => {
        if (!topCampaignsData.campaigns.length) return null;
        const categories = topCampaignsData.campaigns.map((row) => row.shortName);
        const series = [
            {
                name: topCampaignsData.metricLabel,
                data: topCampaignsData.campaigns.map((row) => safeNum(row[topCampaignsData.metricKey]))
            }
        ];

        return {
            series,
            options: applyApexBrandGradient({
                chart: { type: 'bar', toolbar: { show: false } },
                plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 4 } },
                dataLabels: { enabled: false },
                colors: [theme.palette.primary.main],
                xaxis: {
                    categories,
                    labels: {
                        formatter: (val) => topCampaignsData.formatValue(val)
                    }
                },
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => topCampaignsData.formatValue(val)
                })
            }, theme, 'bar')
        };
    }, [topCampaignsData, theme.palette.primary.main]);

    const campaignEfficiencyChart = useMemo(() => {
        if (!campaignRows.length) return null;
        const hasRevenue = showRevenue && campaignRows.some((row) => row.revenue > 0);
        const metricKey = awarenessMode ? 'clicks' : (hasRevenue ? 'revenue' : 'conversions');
        const ranked = [...campaignRows]
            .sort((a, b) => safeNum(b[metricKey]) - safeNum(a[metricKey]))
            .slice(0, 20);

        const channelColors = {
            display: reportColors.revenue,
            native: reportColors.conversions,
            video: reportColors.categories[2],
            audio: reportColors.categories[3]
        };

        const channelMap = new Map();
        ranked.forEach((row) => {
            const key = String(row.channel || 'Other').toLowerCase();
            const list = channelMap.get(key) || [];
            const sizeValue = awarenessMode ? safeNum(row.impressions) : (showCost ? safeNum(row.cost) : safeNum(row.conversions));
            list.push({
                x: safeNum(row.ctr),
                y: awarenessMode ? safeNum(row.clicks) : safeNum(row.convrate),
                z: sizeValue > 0 ? sizeValue : 1,
                name: row.name,
                channel: row.channel,
                impressions: row.impressions,
                clicks: row.clicks,
                conversions: row.conversions,
                revenue: row.revenue,
                cost: row.cost
            });
            channelMap.set(key, list);
        });

        const series = Array.from(channelMap.entries()).map(([key, data]) => ({
            name: key.charAt(0).toUpperCase() + key.slice(1),
            data
        }));

        const colors = series.map((s) => channelColors[s.name.toLowerCase()] || theme.palette.info.main);
        const sizeLabel = awarenessMode ? 'Impressions' : (showCost ? 'Spend' : 'Conversions');
        const allPoints = series.flatMap((item) => item.data);
        const avgCtr = allPoints.length
            ? allPoints.reduce((sum, item) => sum + safeNum(item.x), 0) / allPoints.length
            : null;
        const avgYAxis = allPoints.length
            ? allPoints.reduce((sum, item) => sum + safeNum(item.y), 0) / allPoints.length
            : null;

        return {
            series,
            options: {
                chart: { type: 'bubble', toolbar: { show: false } },
                colors,
                dataLabels: { enabled: false },
                legend: { position: 'bottom' },
                annotations: {
                    xaxis: avgCtr !== null ? [{
                        x: avgCtr,
                        borderColor: theme.palette.divider,
                        strokeDashArray: 4,
                        label: {
                            text: `Avg CTR ${avgCtr.toFixed(1)}%`,
                            style: { background: theme.palette.background.paper, color: theme.palette.text.primary }
                        }
                    }] : [],
                    yaxis: avgYAxis !== null ? [{
                        y: avgYAxis,
                        borderColor: theme.palette.divider,
                        strokeDashArray: 4,
                        label: {
                            text: awarenessMode ? `Avg Clicks ${formatNumber(avgYAxis)}` : `Avg Conv ${avgYAxis.toFixed(1)}%`,
                            style: { background: theme.palette.background.paper, color: theme.palette.text.primary }
                        }
                    }] : []
                },
                xaxis: {
                    title: { text: 'CTR' },
                    labels: { formatter: (val) => `${Number(val).toFixed(1)}%` }
                },
                yaxis: {
                    title: { text: awarenessMode ? 'Clicks' : 'Conversion Rate' },
                    labels: {
                        formatter: (val) => (awarenessMode ? formatNumber(val) : `${Number(val).toFixed(1)}%`)
                    }
                },
                tooltip: {
                    custom: ({ seriesIndex, dataPointIndex, w }) => {
                        const point = w?.config?.series?.[seriesIndex]?.data?.[dataPointIndex];
                        if (!point) return '';
                        const rows = [
                            { label: 'Channel', value: point.channel || '—' },
                            { label: 'CTR', value: `${Number(point.x || 0).toFixed(2)}%` },
                            { label: awarenessMode ? 'Clicks' : 'Conv Rate', value: awarenessMode ? formatNumber(point.y) : `${Number(point.y || 0).toFixed(2)}%` },
                            { label: 'Impressions', value: formatNumber(point.impressions) },
                            { label: 'Clicks', value: formatNumber(point.clicks) },
                            { label: 'Conversions', value: formatNumber(point.conversions) },
                            ...(showCost ? [{ label: 'Spend', value: formatCurrency(point.cost) }] : []),
                            ...(showRevenue ? [{ label: 'Revenue', value: formatCurrency(point.revenue) }] : []),
                            {
                                label: sizeLabel,
                                value: awarenessMode
                                    ? formatNumber(point.impressions)
                                    : (showCost ? formatCurrency(point.cost) : formatNumber(point.conversions))
                            }
                        ];
                        return renderTrendTooltip(theme, {
                            title: point.name || 'Campaign',
                            rows,
                            minWidth: 240
                        });
                    }
                }
            }
        };
    }, [campaignRows, formatNumber, formatCurrency, awarenessMode, showCost, showRevenue, theme.palette]);


    const overviewHeroMetrics = useMemo(() => {
        if (!combined) return [];
        const metrics = [
            { label: 'Impressions', value: combined.currentTotals.impressions, format: 'number', helper: 'Delivered impressions' },
            { label: 'Clicks', value: combined.currentTotals.clicks, format: 'number', helper: 'Traffic generated' },
            { label: 'CTR', value: combined.currentTotals.ctr, format: 'percent', helper: 'Click-through rate' },
            ...(showCost ? [{ label: 'CPM', value: combined.currentTotals.cpm, format: 'currency', helper: 'Cost per thousand impressions' }] : []),
            { label: 'Conversions', value: combined.currentTotals.conversions, format: 'number', helper: 'Recorded conversions' }
        ];
        if (showRevenue) {
            metrics.push({ label: 'Revenue', value: combined.currentTotals.revenue, format: 'currency', helper: 'Attributed revenue' });
        }
        metrics.push({ label: 'Conversion Rate', value: combined.currentTotals.convrate, format: 'percent', helper: 'Conversions / clicks' });
        if (showRevenue) {
            metrics.push({ label: 'Avg Transaction', value: combined.currentTotals.avgGift, format: 'currency', helper: 'Average order value' });
        }
        return metrics;
    }, [combined, showCost, showRevenue]);

    const overviewHeroChips = useMemo(() => {
        const rankByRevenue = showRevenue && channelSummary.some((channel) => channel.revenue > 0) && campaignRows.some((row) => row.revenue > 0);
        const topChannel = channelSummary
            .slice()
            .sort((a, b) => (
                awarenessMode
                    ? ((b.clicks - a.clicks) || (b.impressions - a.impressions))
                    : (rankByRevenue ? (b.revenue - a.revenue) : (b.conversions - a.conversions))
            ))[0];
        const topCampaign = campaignRows
            .slice()
            .sort((a, b) => (
                awarenessMode
                    ? ((b.clicks - a.clicks) || (b.impressions - a.impressions))
                    : (rankByRevenue ? (b.revenue - a.revenue) : (b.conversions - a.conversions))
            ))[0];
        return [
            { label: 'Channels', value: channelSummary.length },
            { label: 'Campaigns', value: campaignRows.length },
            { label: 'Trend Points', value: combined?.trendRows?.length || 0 },
            ...(safeNum(footfallData?.totals?.footfall) > 0 ? [{ label: 'Visits', value: safeNum(footfallData.totals.footfall) }] : []),
            ...(topChannel ? [{ label: 'Top Channel', value: topChannel.label }] : []),
            ...(topCampaign ? [{ label: 'Top Campaign', value: truncateLabel(topCampaign.name, 24) }] : [])
        ];
    }, [campaignRows, channelSummary, combined?.trendRows?.length, footfallData, awarenessMode, showRevenue]);

    const retailCampaignTableRows = useMemo(() => (
        retailMode
            ? campaignRows.map((row) => ({
                ...row,
                completedDisplay: (row.channelKey === 'audio' || row.channelKey === 'video' || row.channelKey === 'ctv')
                    ? row.completedCount
                    : null,
                completionRateDisplay: (row.channelKey === 'audio' || row.channelKey === 'video' || row.channelKey === 'ctv')
                    ? row.completionRate
                    : null
            }))
            : []
    ), [campaignRows, retailMode]);

    const retailCompletionTotals = useMemo(() => ({
        completedCount: retailCampaignTableRows.reduce((sum, row) => sum + safeNum(row.completedDisplay), 0)
    }), [retailCampaignTableRows]);

    const retailCampaignColumns = useMemo(() => ([
        {
            header: 'Channel',
            accessorKey: 'channel',
            size: 120,
            Cell: ({ row }) => (
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {row.original.channel}
                </Typography>
            ),
            Footer: () => null
        },
        {
            header: 'Campaign',
            accessorKey: 'name',
            minSize: 280,
            size: 360,
            Cell: ({ row }) => (
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {row.original.name}
                </Typography>
            ),
            Footer: () => <Typography variant="body2" sx={{ fontWeight: 800 }}>Totals</Typography>
        },
        {
            header: 'Impressions',
            accessorKey: 'impressions',
            Cell: ({ row }) => <NumericFormat value={row.original.impressions} displayType="text" thousandSeparator="," />,
            Footer: () => <NumericFormat value={retailCampaignTableRows.reduce((sum, row) => sum + row.impressions, 0)} displayType="text" thousandSeparator="," />
        },
        {
            header: 'Clicks',
            accessorKey: 'clicks',
            Cell: ({ row }) => <NumericFormat value={row.original.clicks} displayType="text" thousandSeparator="," />,
            Footer: () => <NumericFormat value={retailCampaignTableRows.reduce((sum, row) => sum + row.clicks, 0)} displayType="text" thousandSeparator="," />
        },
        {
            header: 'CTR',
            accessorKey: 'ctr',
            Cell: ({ row }) => <NumericFormat value={row.original.ctr} displayType="text" decimalScale={2} suffix="%" />,
            Footer: () => {
                const impressions = retailCampaignTableRows.reduce((sum, row) => sum + row.impressions, 0);
                const clicks = retailCampaignTableRows.reduce((sum, row) => sum + row.clicks, 0);
                const ctr = impressions ? (clicks / impressions) * 100 : 0;
                return <NumericFormat value={ctr} displayType="text" decimalScale={2} suffix="%" />;
            }
        },
        {
            header: 'Conversions',
            accessorKey: 'conversions',
            Cell: ({ row }) => <NumericFormat value={row.original.conversions} displayType="text" thousandSeparator="," />,
            Footer: () => <NumericFormat value={retailCampaignTableRows.reduce((sum, row) => sum + row.conversions, 0)} displayType="text" thousandSeparator="," />
        },
        {
            header: 'Conv %',
            accessorKey: 'convrate',
            Cell: ({ row }) => <NumericFormat value={row.original.convrate} displayType="text" decimalScale={2} suffix="%" />,
            Footer: () => {
                const clicks = retailCampaignTableRows.reduce((sum, row) => sum + row.clicks, 0);
                const conversions = retailCampaignTableRows.reduce((sum, row) => sum + row.conversions, 0);
                const convRate = clicks ? (conversions / clicks) * 100 : 0;
                return <NumericFormat value={convRate} displayType="text" decimalScale={2} suffix="%" />;
            }
        },
        {
            header: 'Completed Plays / Views',
            accessorKey: 'completedDisplay',
            size: 180,
            Cell: ({ row }) => (
                row.original.completedDisplay == null
                    ? '—'
                    : <NumericFormat value={row.original.completedDisplay} displayType="text" thousandSeparator="," />
            ),
            Footer: () => retailCompletionTotals.completedCount > 0
                ? <NumericFormat value={retailCompletionTotals.completedCount} displayType="text" thousandSeparator="," />
                : null
        },
        {
            header: 'Completion Rate',
            accessorKey: 'completionRateDisplay',
            size: 150,
            Cell: ({ row }) => (
                row.original.completionRateDisplay == null
                    ? '—'
                    : <NumericFormat value={row.original.completionRateDisplay} displayType="text" decimalScale={2} suffix="%" />
            ),
            Footer: () => null
        }
    ]), [retailCampaignTableRows, retailCompletionTotals.completedCount]);

    const isLoading = loadingReport || appLoading;

    const formatPulseMetric = (metric) => ({ label: metric.label, value: metric.format === 'currency' ? formatCurrency(metric.value) : metric.format === 'percent' ? formatPercent(metric.value) : formatNumber(metric.value), detail: metric.helper });
    const pulseMetrics = (labels) => overviewHeroMetrics.filter((metric) => labels.includes(metric.label)).map(formatPulseMetric);

    return (
        <ThemeProvider theme={theme}><Box sx={pageShellSx}>
            <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', minWidth: 0, gap: 2 }}>
                <Box sx={tabsShellSx}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1,
                            width: '100%'
                        }}
                    >
                        <Box sx={{ flex: '1 1 320px' }} >
                            <ResponsiveTabs value={value} indicatorColor="primary" onChange={handleChange} aria-label="Display report tabs" variant="standard" scrollButtons="auto" sx={tabsSx}>
                                <Tab label="Performance Overview" value="overview" {...a11yProps('overview')} />
                                {hasFootfallGraphqlToken && <Tab label="Locations" value="footfall" {...a11yProps('footfall')} />}
                                {channelAvailability.native && <Tab label="Native Ads" value="native" {...a11yProps('native')} />}
                                {channelAvailability.display && <Tab label="Display Ads" value="display" {...a11yProps('display')} />}
                                {channelAvailability.video && <Tab label="Video Ads" value="video" {...a11yProps('video')} />}
                                {channelAvailability.audio && <Tab label="Audio Ads" value="audio" {...a11yProps('audio')} />}
                                {channelAvailability.ctv && <Tab label="CTV Ads" value="ctv" {...a11yProps('ctv')} />}
                                <Tab label="Notes" value="notes" {...a11yProps('notes')} />
                            </ResponsiveTabs>
                        </Box>
                    </Box>
                </Box>

                {value !== 'overview' ? <ReportSectionIntro report="Display & programmatic" title={{ display: 'Display ads', native: 'Native ads', video: 'Video ads', audio: 'Audio ads', ctv: 'Connected TV', footfall: 'Locations', notes: 'Notes' }[value]} description="Explore channel activity and the detail behind your results." dateLabel={pDate} /> : null}
                {value === 'overview' ? (
                    <ReportPerformanceOverview
                        eyebrow={retailMode ? "Retail display & programmatic" : "Display & programmatic"}
                        title={retailMode ? "From exposure to response" : "From reach to results"}
                        description={retailMode ? "Compare delivery, clicks, and recorded actions across your retail media channels." : "Compare delivery, response, and outcomes across your advertising channels."}

                        loading={isLoading && !combined}
                        stages={[
                            { label: retailMode ? 'Visibility' : 'Delivery', description: 'Exposure across active channels.', metrics: pulseMetrics(retailMode ? ['Impressions'] : ['Impressions', 'Clicks']) },
                            { label: retailMode ? 'Traffic' : 'Response', description: 'Traffic generated by your ads.', metrics: pulseMetrics(retailMode ? ['Clicks', 'CTR'] : ['CTR', 'Conversion Rate']) },
                            { label: retailMode ? 'Response' : 'Outcomes', description: retailMode ? 'Recorded actions following a click.' : 'Recorded actions and their value.', metrics: pulseMetrics(['Conversions', ...(retailMode ? ['Conversion Rate'] : showRevenue ? ['Revenue'] : [])]) }
                        ]}
                        supportMetrics={overviewHeroMetrics.filter((metric) => ['CPM', 'Avg Transaction'].includes(metric.label)).map(formatPulseMetric)}
                        highlights={overviewHeroChips.map((chip) => ({ label: chip.label, title: String(chip.value), detail: 'Within the selected period' }))}
                    />
                ) : null}


                <Box className="stackadapt-executive-content">
                {(data ? <>
                    <TabPanel value={value} index="overview" id="overview">
                        <Box sx={{ pt: 1 }}>
                            {combined && !isLoading ? (
                                <>
                                    {channelSummary.length > 0 && (
                                        <Box sx={{ mt: 2 }}>

                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                {channelSummary.map((channel) => (
                                                    <Card
                                                        key={channel.key}
                                                        variant="outlined"
                                                        sx={{
                                                            ...surfaceCardSx,
                                                            flexGrow: 1,
                                                            flexBasis: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(25% - 16px)' },
                                                            minWidth: 0,
                                                            maxWidth: '100%'
                                                        }}
                                                    >
                                                        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 2 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                {channel.label}
                                                            </Typography>
                                                            <Divider sx={{ my: 0.25 }} />
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                <Typography variant="caption" color="text.secondary">Impressions</Typography>
                                                                <NumericFormat value={channel.impressions} displayType="text" thousandSeparator="," />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                <Typography variant="caption" color="text.secondary">Clicks</Typography>
                                                                <NumericFormat value={channel.clicks} displayType="text" thousandSeparator="," />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                <Typography variant="caption" color="text.secondary">CTR</Typography>
                                                                <NumericFormat value={channel.ctr} displayType="text" decimalScale={2} suffix="%" />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                <Typography variant="caption" color="text.secondary">Conversions</Typography>
                                                                <NumericFormat value={channel.conversions} displayType="text" thousandSeparator="," />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                <Typography variant="caption" color="text.secondary">Conv Rate</Typography>
                                                                <NumericFormat value={channel.convrate} displayType="text" decimalScale={2} suffix="%" />
                                                            </Box>
                                                            {showCost && (
                                                                <>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                        <Typography variant="caption" color="text.secondary">Cost</Typography>
                                                                        <NumericFormat value={channel.cost} displayType="text" thousandSeparator="," prefix="$" decimalScale={2} />
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                        <Typography variant="caption" color="text.secondary">CPC</Typography>
                                                                        <NumericFormat value={channel.cpc} displayType="text" thousandSeparator="," prefix="$" decimalScale={2} />
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                        <Typography variant="caption" color="text.secondary">CPM</Typography>
                                                                        <NumericFormat value={channel.cpm} displayType="text" thousandSeparator="," prefix="$" decimalScale={2} />
                                                                    </Box>
                                                                </>
                                                            )}
                                                            {showRevenue && (
                                                                <>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                        <Typography variant="caption" color="text.secondary">Revenue</Typography>
                                                                        <NumericFormat value={channel.revenue} displayType="text" thousandSeparator="," prefix="$" decimalScale={2} />
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                        <Typography variant="caption" color="text.secondary">Avg Transaction</Typography>
                                                                        <NumericFormat value={channel.avgGift} displayType="text" thousandSeparator="," prefix="$" decimalScale={2} />
                                                                    </Box>
                                                                </>
                                                            )}
                                                            {showCost && (
                                                                <>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                        <Typography variant="caption" color="text.secondary">ROAS</Typography>
                                                                        <NumericFormat value={channel.roas} displayType="text" decimalScale={2} suffix="x" />
                                                                    </Box>
                                                                </>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {(topAdsByCtr.length > 0 || (awarenessMode ? topAdsByImpressions.length > 0 : topAdsByConvRate.length > 0)) && (
                                        <Box sx={{ mt: 3 }}>

                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: 'calc(50% - 16px)' } }}>
                                                    <Card variant="outlined" sx={surfaceCardSx}>
                                                        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                Top Ads by CTR
                                                            </Typography>
                                                            {topCtrWinner ? (
                                                                <Box
                                                                    sx={{
                                                                        border: '1px solid',
                                                                        borderColor: 'divider',
                                                                        borderRadius: 1,
                                                                        p: 1.5,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: 1,
                                                                        flex: '1 1 auto'
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            gap: 1,
                                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                                            alignItems: 'stretch'
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                border: '1px solid',
                                                                                borderColor: 'divider',
                                                                                borderRadius: 1,
                                                                                overflow: 'hidden',
                                                                                minHeight: 100,
                                                                                background: '#fff',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                flex: { xs: '0 0 auto', sm: '0 0 140px' },
                                                                                width: { xs: '100%', sm: 140 },
                                                                                cursor: getAssetPreviewUrl(topCtrWinner.creative) ? 'zoom-in' : 'default'
                                                                            }}
                                                                            onClick={() => openLightbox(getAssetPreviewUrl(topCtrWinner.creative), getCreativeLabel(topCtrWinner.creative))}
                                                                        >
                                                                            {getAssetPreviewUrl(topCtrWinner.creative) ? (
                                                                                <Box
                                                                                    component="img"
                                                                                    src={getAssetPreviewUrl(topCtrWinner.creative)}
                                                                                    alt={`Asset preview for ${getCreativeLabel(topCtrWinner.creative)}`}
                                                                                    loading="lazy"
                                                                                    onError={(event) => {
                                                                                        event.currentTarget.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                                                                                    }}
                                                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                />
                                                                            ) : (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    No preview
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    CTR {formatPercent(topCtrWinner.metrics.ctr)}
                                                                                </Typography>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {formatNumber(topCtrWinner.metrics.clicks)} clicks
                                                                                </Typography>
                                                                            </Box>
                                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                                {getCreativeLabel(topCtrWinner.creative)}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Group: {topCtrGroup?.name || topCtrGroup?.NAME} · {topCtrGroup?.channel}
                                                                            </Typography>
                                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                                                <Chip size="small" label={`Impr ${formatNumber(topCtrWinner.metrics.impressions)}`} />
                                                                                <Chip size="small" label={`Conv ${formatNumber(topCtrWinner.metrics.conversions)}`} />
                                                                                {showCost ? <Chip size="small" label={`Spend ${formatCurrency(topCtrWinner.metrics.cost)}`} /> : null}
                                                                                {showRevenue ? <Chip size="small" label={`Revenue ${formatCurrency(topCtrWinner.metrics.revenue)}`} /> : null}
                                                                                {showCost ? <Chip size="small" label={`ROAS ${topCtrWinner.metrics.roas.toFixed(2)}x`} /> : null}
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {topCtrGroup
                                                                        ? (ctrCreatives === undefined ? 'Loading creative details...' : 'No creative data available.')
                                                                        : 'No CTR data available.'}
                                                                </Typography>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </Box>
                                                <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: 'calc(50% - 16px)' } }}>
                                                    <Card variant="outlined" sx={surfaceCardSx}>
                                                        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                {awarenessMode ? 'Top Ads by Impressions' : 'Top Ads by Conversion Rate'}
                                                            </Typography>
                                                            {topSecondaryWinner ? (
                                                                <Box
                                                                    sx={{
                                                                        border: '1px solid',
                                                                        borderColor: 'divider',
                                                                        borderRadius: 1,
                                                                        p: 1.5,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: 1
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            gap: 1,
                                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                                            alignItems: 'stretch'
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                border: '1px solid',
                                                                                borderColor: 'divider',
                                                                                borderRadius: 1,
                                                                                overflow: 'hidden',
                                                                                minHeight: 100,
                                                                                background: '#fff',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                flex: { xs: '0 0 auto', sm: '0 0 140px' },
                                                                                width: { xs: '100%', sm: 140 },
                                                                                cursor: getAssetPreviewUrl(topSecondaryWinner.creative) ? 'zoom-in' : 'default'
                                                                            }}
                                                                            onClick={() => openLightbox(getAssetPreviewUrl(topSecondaryWinner.creative), getCreativeLabel(topSecondaryWinner.creative))}
                                                                        >
                                                                            {getAssetPreviewUrl(topSecondaryWinner.creative) ? (
                                                                                <Box
                                                                                    component="img"
                                                                                    src={getAssetPreviewUrl(topSecondaryWinner.creative)}
                                                                                    alt={`Asset preview for ${getCreativeLabel(topSecondaryWinner.creative)}`}
                                                                                    loading="lazy"
                                                                                    onError={(event) => {
                                                                                        event.currentTarget.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                                                                                    }}
                                                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                />
                                                                            ) : (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    No preview
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {awarenessMode
                                                                                        ? `Impr ${formatNumber(topSecondaryWinner.metrics.impressions)}`
                                                                                        : `Conv ${formatPercent(topSecondaryWinner.metrics.convRate)}`}
                                                                                </Typography>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {awarenessMode
                                                                                        ? `${formatNumber(topSecondaryWinner.metrics.clicks)} clicks`
                                                                                        : `${formatNumber(topSecondaryWinner.metrics.conversions)} conv`}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                                {getCreativeLabel(topSecondaryWinner.creative)}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Group: {topSecondaryGroup?.name || topSecondaryGroup?.NAME} · {topSecondaryGroup?.channel}
                                                                            </Typography>
                                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                                                <Chip size="small" label={`Clicks ${formatNumber(topSecondaryWinner.metrics.clicks)}`} />
                                                                                <Chip size="small" label={`Impr ${formatNumber(topSecondaryWinner.metrics.impressions)}`} />
                                                                                {awarenessMode ? (
                                                                                    <Chip size="small" label={`CTR ${formatPercent(topSecondaryWinner.metrics.ctr)}`} />
                                                                                ) : null}
                                                                                {showRevenue ? (
                                                                                    <>
                                                                                        <Chip size="small" label={`Revenue ${formatCurrency(topSecondaryWinner.metrics.revenue)}`} />
                                                                                        <Chip size="small" label={`Avg ${formatCurrency(topSecondaryWinner.metrics.avgGift)}`} />
                                                                                    </>
                                                                                ) : null}
                                                                                {showCost ? <Chip size="small" label={`ROAS ${topSecondaryWinner.metrics.roas.toFixed(2)}x`} /> : null}
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {topSecondaryGroup
                                                                        ? (secondaryCreatives === undefined ? 'Loading creative details...' : 'No creative data available.')
                                                                        : (awarenessMode ? 'No impression data available.' : 'No conversion rate data available.')}
                                                                </Typography>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}

                                    {combined.trendRows && combined.trendRows.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Box sx={{ ...surfaceCardSx, p: { xs: 1, sm: 1.25 } }}>
                                                <TrendChart reportStyle
                                                    trendData={combined.trendRows}
                                                    showCost={showCost}
                                                    showRevenue={showRevenue}
                                                    noteType="stackadapt"
                                                    noteView="a7edd5c0-7a77-49ed-86ad-6fe3066a0f59"
                                                />
                                            </Box>
                                        </Box>
                                    )}

                                    {retailMode && retailCampaignTableRows.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            <Card variant="outlined" sx={surfaceCardSx}>
                                                <CardHeader
                                                    title="All Programmatic Campaigns"
                                                    subheader="One campaign table across Display, Native, Audio, Video, and CTV. Completion metrics populate for audio, video, and CTV when the source data provides them."
                                                />
                                                <CardContent sx={{ p: { xs: 1, sm: 1.25 } }}>
                                                    <ViMarcTableCard
                                                        title="All Programmatic Campaigns"
                                                        data={retailCampaignTableRows}
                                                        columns={retailCampaignColumns}
                                                        appendDefaultMetrics={false}
                                                        compact={false}
                                                        getRowId={(row, index) => row.id || `${row.channelKey || row.channel}-${index}`}
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    )}

                                    {(campaignEfficiencyChart || topCampaignChart) && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                {awarenessMode ? 'Campaign Reach and Engagement' : 'Campaign Performance'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        flexGrow: 1,
                                                        flexBasis: { xs: '100%', lg: 'calc(60% - 16px)' },
                                                        minWidth: { xs: '100%', lg: 420 }
                                                    }}
                                                >
                                                    <Card variant="outlined" sx={surfaceCardSx}>
                                                        <CardHeader
                                                            title={awarenessMode ? 'Reach and Engagement Map' : 'Efficiency Map'}
                                                            subheader={awarenessMode ? 'CTR vs Clicks · Bubble size = Impressions' : `CTR vs Conversion Rate · Bubble size = ${showCost ? 'Spend' : 'Conversions'}`}
                                                        />
                                                        <CardContent>
                                                            {campaignEfficiencyChart ? (
                                                                <ReactApexChart
                                                                    options={styleReportChart(theme, campaignEfficiencyChart.options)}
                                                                    series={campaignEfficiencyChart.series}
                                                                    type="bubble"
                                                                    height={320}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No campaign efficiency data available.
                                                                </Typography>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        flexGrow: 1,
                                                        flexBasis: { xs: '100%', lg: 'calc(40% - 16px)' },
                                                        minWidth: { xs: '100%', lg: 320 }
                                                    }}
                                                >
                                                    <Card variant="outlined" sx={surfaceCardSx}>
                                                        <CardHeader
                                                            title={`Top Campaigns by ${topCampaignsData.metricLabel}`}
                                                            subheader="Across channels"
                                                            action={(
                                                                <FormControl size="small" sx={{ minWidth: 170 }}>
                                                                    <Select
                                                                        value={topCampaignMetric || defaultTopMetric || ''}
                                                                        onChange={(event) => setTopCampaignMetric(event.target.value)}
                                                                        displayEmpty
                                                                        renderValue={(selected) => {
                                                                            const selectedKey = selected || defaultTopMetric;
                                                                            const choice = metricChoices.find((item) => item.key === selectedKey);
                                                                            return choice ? choice.label : 'Metric';
                                                                        }}
                                                                    >
                                                                        {metricChoices.map((choice) => (
                                                                            <MenuItem key={choice.key} value={choice.key}>
                                                                                {choice.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            )}
                                                        />
                                                        <CardContent>
                                                            {topCampaignChart ? (
                                                                <ReactApexChart
                                                                    options={styleReportChart(theme, { ...topCampaignChart.options, colors: [reportColors.revenue] })}
                                                                    series={topCampaignChart.series}
                                                                    type="bar"
                                                                    height={320}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No top campaign data available.
                                                                </Typography>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                renderLoadingSkeleton()
                            )}
                        </Box>
                    </TabPanel>

                    {hasFootfallGraphqlToken && (
                        <TabPanel value={value} index="footfall" id="footfall">
                            <Footfall
                                data={footfallData}
                                loading={isLoading}
                                surfaceCardSx={surfaceCardSx}
                                showCost={showCost}
                            />
                        </TabPanel>
                    )}

                    {channelAvailability.native && (
                        <TabPanel value={value} index="native" id="native">
                            <Native
                                title={"Native"}
                                data={data}
                                campaignData={campaignData}
                                reportProfile={effectiveReportProfile}
                                client={client}
                                loading={isLoading}
                                awarenessMode={awarenessMode}
                            />
                        </TabPanel>
                    )}

                    {channelAvailability.display && (
                        <TabPanel value={value} index="display" id="display">
                            <Display
                                title={"Display"}
                                data={data}
                                campaignData={campaignData}
                                reportProfile={effectiveReportProfile}
                                client={client}
                                loading={isLoading}
                                awarenessMode={awarenessMode}
                            />
                        </TabPanel>
                    )}

                    {channelAvailability.video && (
                        <TabPanel value={value} index="video" id="video">
                            <Video
                                title={"Video"}
                                data={data}
                                campaignData={campaignData}
                                reportProfile={effectiveReportProfile}
                                client={client}
                                loading={isLoading}
                                awarenessMode={awarenessMode}
                            />
                        </TabPanel>
                    )}

                    {channelAvailability.audio && (
                        <TabPanel value={value} index="audio" id="audio">
                            <Audio
                                title={"Audio"}
                                data={data}
                                campaignData={campaignData}
                                reportProfile={effectiveReportProfile}
                                client={client}
                                loading={isLoading}
                                awarenessMode={awarenessMode}
                            />
                        </TabPanel>
                    )}

                    {channelAvailability.ctv && (
                        <TabPanel value={value} index="ctv" id="ctv">
                            <Video
                                title={"CTV"}
                                channelKey="ctv"
                                data={data}
                                campaignData={campaignData}
                                reportProfile={effectiveReportProfile}
                                client={client}
                                loading={isLoading}
                                awarenessMode={awarenessMode}
                            />
                        </TabPanel>
                    )}

                    <TabPanel value={value} index="notes" id="notes">
                        <Comments eDate={props.eDate} sDate={props.sDate} admintoken={admintoken} view={(admintoken >= 6 ? "5af0a295-8b57-4ccb-8cc8-d54e5bb278e6" : "a7edd5c0-7a77-49ed-86ad-6fe3066a0f59")} type="stackadapt" setLoading={props.setLoading}  ></Comments>

                    </TabPanel>
                </>
                    : renderLoadingSkeleton())}
                </Box>


            </Box>

            <Dialog open={lightboxOpen} onClose={closeLightbox} maxWidth="md" fullWidth PaperProps={{ sx: surfaceCardSx }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `linear-gradient(135deg, ${alpha(primaryColor, 0.14)} 0%, ${alpha(secondaryColor, 0.16)} 100%)`, borderBottom: `1px solid ${alpha(primaryColor, 0.12)}` }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {lightboxTitle}
                    </Typography>
                    <IconButton onClick={closeLightbox} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {lightboxSrc ? (
                        <Box
                            component="img"
                            src={lightboxSrc}
                            alt={lightboxTitle}
                            sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                            onError={(event) => {
                                event.currentTarget.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                            }}
                        />
                    ) : null}
                </DialogContent>
            </Dialog>

            <ReportQuestionAssistant
                reportType="display"
                reportTitle="Display"
                profile={props.reportProfile}
                contextPayload={stackAdaptAssistantConfig}
            />

        </Box></ThemeProvider>
    );
};

StackAdapt.propTypes = {

};

export default StackAdapt;
